import {
    eachDayOfInterval,
    eachMonthOfInterval,
    eachWeekOfInterval,
    endOfDay,
    formatISO,
    isWeekend,
    lastDayOfMonth,
    previousFriday,
    startOfDay,
    startOfMonth,
    startOfYear,
    subDays,
    subMinutes,
    subMonths,
    subWeeks,
    subYears
} from 'date-fns';
import { BehaviorSubject, ObservedValueOf } from 'rxjs';

export type RangeScale = 'endOfDay' | 'endOfWeek' | 'endOfMonth';

export interface DateMap {
    [key: string]: {
        [key: string]: number;
    };
}

export interface Period {
    name:
        | '1D'
        | '1W'
        | 'MTD'
        | '1M'
        | '3M'
        | 'YTD'
        | '1Y'
        | '3Y'
        | '5Y'
        | 'All'
        | 'Custom';
    start: Date;
    end: Date;
    context?: string;
    showDateSelector?: boolean;
}

export const getPeriodsMap = (
    config = { dayOffset: 0 }
): { [key in Period['name']]: Period } => {
    return {
        '1D': {
            name: '1D',
            start: new Date(
                subMinutes(
                    startOfDay(subDays(new Date(), config.dayOffset)),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(
                    endOfDay(subDays(new Date(), config.dayOffset)),
                    new Date().getTimezoneOffset()
                )
            )
        },
        '1W': {
            name: '1W',
            start: new Date(
                subMinutes(
                    subWeeks(startOfDay(new Date()), 1),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        MTD: {
            name: 'MTD',
            start: new Date(
                subMinutes(
                    startOfMonth(startOfDay(new Date())),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        '1M': {
            name: '1M',
            start: new Date(
                subMinutes(
                    subMonths(startOfDay(new Date()), 1),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        '3M': {
            name: '3M',
            start: new Date(
                subMinutes(
                    startOfDay(subMonths(new Date(), 3)),
                    subMonths(new Date(), 3).getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        YTD: {
            name: 'YTD',
            start: new Date(
                subMinutes(
                    startOfDay(startOfYear(new Date())),
                    startOfYear(new Date()).getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        '1Y': {
            name: '1Y',
            start: new Date(
                subMinutes(
                    subYears(startOfDay(new Date()), 1),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        '3Y': {
            name: '3Y',
            start: new Date(
                subMinutes(
                    subYears(startOfDay(new Date()), 3),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        '5Y': {
            name: '5Y',
            start: new Date(
                subMinutes(
                    subYears(startOfDay(new Date()), 5),
                    new Date().getTimezoneOffset()
                )
            ),
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        All: {
            name: 'All',
            start: null,
            end: new Date(
                subMinutes(endOfDay(new Date()), new Date().getTimezoneOffset())
            )
        },
        Custom: {
            name: 'Custom',
            start: null,
            end: null,
            showDateSelector: true
        }
    };
};

export const getPeriods = (): Array<Period> => {
    return Object.values<Period>(getPeriodsMap() as any);
};

export const getScales = () => {
    return [
        {
            name: 'Daily',
            value: 'endOfDay'
        },
        {
            name: 'Weekly',
            value: 'endOfWeek'
        },
        {
            name: 'Monthly',
            value: 'endOfMonth'
        }
    ];
};

/** Array to date map
 * Transform array to date map
 * @param {Array<Array<[number, number]>> | Array<[number, number]> | Array<Array<number>> } data - Data to be transformed.
 * @param {{ [key: string]: number } | string } name - Name to use for data map, if nested array it should be an index map with key as name and value as index for data in array.
 * @returns { DateMap } - Object with name(s) as key(s) and data mapped by date to key value as an object.
 */
export const arrayToDateMap: {
    (data: Array<Array<[number, number]>>, indexMap: { [key: string]: number });
    (data: Array<[number, number]>, name: string);
    (data: Array<Array<number>>, name: string);
} = (data, name): DateMap => {
    let dateMap: DateMap = {};
    if (typeof name === 'string') {
        dateMap[name] = {};
        for (let i = 0; i < data?.length; i++) {
            const dateString = formatISO(data[i][0], { representation: 'date' });
            dateMap[name][dateString] = data[i][1];
        }
        return dateMap;
    } else {
        const groups = Object.entries<number>(name);
        dateMap = groups.reduce((groups, [name, index]) => {
            groups[name] = {};
            return groups;
        }, {});
        for (let i = 0; i < data[0].data.length; i++) {
            const dateString = formatISO(data[0].data[i][0], { representation: 'date' });
            for (let y = 0; y < groups.length; y++) {
                const name = groups[y][0];
                const index = groups[y][1] ?? 0;
                dateMap[name][dateString] = data[index]?.data[i][1];
            }
        }
    }
    return dateMap;
};

export const mapDataToRange: {
    (dateMap: { [key: string]: number }, range: Array<Date>);
} = (dateMap, range): Array<number> => {
    const rangeData = [];
    for (let i = 0; i < range.length; i++) {
        const dateString = formatISO(range[i], { representation: 'date' });
        rangeData[i] = dateMap[dateString] || null;
    }
    return rangeData;
};

export const createDateRange = (
    start: Date = subMonths(new Date(), 1),
    end: Date = new Date(),
    scale: RangeScale = 'endOfDay',
    excludeWeekends = false
): Array<Date> => {
    if (scale === 'endOfMonth') {
        let interval = eachMonthOfInterval({
            start,
            end
        });
        interval = interval.map((date, index) => {
            if (index === interval.length - 1) {
                return date;
            }
            let lastDay = lastDayOfMonth(date);
            if (isWeekend(lastDay)) {
                lastDay = previousFriday(lastDay);
            }
            return lastDay;
        });

        if (interval[0] !== start) {
            interval.unshift(start);
        }
        if (interval[interval.length - 1] !== end) {
            interval.push(end);
        }
        return interval;
    } else if (scale === 'endOfWeek') {
        const interval = eachWeekOfInterval(
            {
                start,
                end
            },
            {
                weekStartsOn: 5
            }
        );
        if (interval[0] !== start) {
            interval.unshift(start);
        }
        if (interval[interval.length - 1] !== end) {
            interval.push(end);
        }
        return interval;
    } else {
        const eachDay = eachDayOfInterval({
            start,
            end
        });
        if (excludeWeekends) {
            return eachDay.filter((date) => !isWeekend(date));
        } else {
            return eachDay;
        }
    }
};

export const getDeltaArray = (data: Array<number>): Array<number> => {
    let firstValue;
    const deltaArray = [];
    for (let i = 0; i < data.length; i++) {
        const value = data[i];
        if (!firstValue && !!value) {
            firstValue = value;
        }
        deltaArray[i] = value === null ? null : i === 0 ? 0 : value / firstValue - 1;
    }
    return deltaArray;
};

export const setMaxCulling = (
    period: ObservedValueOf<BehaviorSubject<Partial<Period>>>,
    months: number,
    range: Array<Date>
): { maxCulling: number; format: string } => {
    switch (period.name) {
        case '1W':
        case 'MTD':
        case '1M':
        case '3M':
            return { maxCulling: 17, format: 'd/M' };
        case 'YTD':
            if (months >= 6) {
                return {
                    maxCulling: range.length > 12 ? months : 20,
                    format: 'MMM yy'
                };
            } else {
                return { maxCulling: 17, format: 'd/M' };
            }
        case '1Y':
        case '3Y':
            return { maxCulling: 12, format: 'MMM yy' };
        case 'Custom':
        case 'All':
            if (months >= 6) {
                return {
                    maxCulling: months > 17 ? 17 : months,
                    format: 'MMM yy'
                };
            } else {
                return { maxCulling: 17, format: 'd/M' };
            }
        default:
            return { maxCulling: 12, format: 'MMM yy' };
    }
};
